import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import MobileAppBanner from '../components/MobileAppBanner';
import { getUserFavorites } from '../services/api';
import cacheService from '../services/cacheService';
import POIDetailsSidePanel from '../components/POIDetailsSidePanel';
import AdPageLayout from '../components/AdPageLayout';
import Toast from '../components/Toast';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f5f7f9;
`;

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
  background: linear-gradient(135deg, #f8fafb 0%, #f1f5f7 100%);
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 2.5rem;
`;

const PageTitle = styled.h1`
  color: #1a365d;
  font-family: 'Josefin Sans', sans-serif;
  font-weight: 700;
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  background: linear-gradient(135deg, #316C6E 0%, #254F51 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const PageSubtitle = styled.p`
  color: #64748b;
  font-size: 1.1rem;
  margin: 0;
  font-weight: 400;
`;

const FavoritesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const FavoriteCard = styled.div`
  cursor: pointer;
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.04);

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04);
    border-color: rgba(49, 108, 110, 0.1);
  }

  &:active {
    transform: translateY(-4px);
  }
`;

const FavoriteImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
`;

const FavoriteImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  ${FavoriteCard}:hover & {
    transform: scale(1.05);
  }
`;

const CardBody = styled.div`
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const FavoriteName = styled.h2`
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #1a365d;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const FavoriteDescription = styled.p`
  font-size: 0.95rem;
  color: #64748b;
  margin: 0;
  font-weight: 400;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(237.5px, 1fr));
  gap: 20px;
  padding: 20px;
`;

const SkeletonCard = styled.div`
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
  position: relative;
  height: 100%;
`;

const SkeletonImage = styled.div`
  width: 100%;
  height: 142.5px;
  background: linear-gradient(
    90deg,
    #f0f0f0 25%,
    #e0e0e0 50%,
    #f0f0f0 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  
  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const SkeletonText = styled.div`
  height: ${props => props.height || '20px'};
  width: ${props => props.width || '80%'};
  margin: ${props => props.margin || '10px auto'};
  background: linear-gradient(
    90deg,
    #f0f0f0 25%,
    #e0e0e0 50%,
    #f0f0f0 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
`;


const EmptyMessage = styled.p`
  font-size: 1.2rem;
  text-align: center;
  margin: 20px 0;
`;

const ErrorMessage = styled.p`
  font-size: 1.2rem;
  text-align: center;
  margin: 20px 0;
`;

const LoadingMessage = styled.p`
  font-size: 1.2rem;
  text-align: center;
  margin: 20px 0;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  color: #ccc;
  margin-bottom: 20px;
`;

const EmptyStateTitle = styled.h2`
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 10px;
`;

const EmptyStateText = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 20px;
  max-width: 400px;
  line-height: 1.5;
`;

const ExploreButton = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 25px;
  padding: 12px 24px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #0056b3;
  }
`;

const SkeletonLoading = () => {
  return (
    <SkeletonGrid>
      {[...Array(8)].map((_, index) => (
        <SkeletonCard key={index}>
          <SkeletonImage />
          <div style={{ padding: '10px' }}>
            <SkeletonText height="24px" width="70%" margin="15px auto" />
            <SkeletonText height="16px" width="90%" margin="10px auto" />
          </div>
        </SkeletonCard>
      ))}
    </SkeletonGrid>
  );
};

const FavoritesPage = () => {
  const [favorites, setFavorites] = useState([]);
  const [selectedPOI, setSelectedPOI] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const { language } = useLanguage();
  const t = translations[language].pages.favorites;

  const getLocalizedField = (item, field) => {
    if (language === 'es') {
      return item[`${field}_es`] || item[field];
    }
    return item[field];
  };

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    const startTime = Date.now();
    try {
      // Use the cache service for favorites
      const data = await cacheService.getFavorites();
      setFavorites(data);
      setError(null);
    } catch (err) {
      setError(translations[language].common.error);
      console.error('Error fetching favorites:', err);
    } finally {
      const endTime = Date.now();
      const loadingTime = endTime - startTime;
      const minimumLoadingTime = 1000; // 1 second in milliseconds

      if (loadingTime < minimumLoadingTime) {
        setTimeout(() => {
          setIsLoading(false);
        }, minimumLoadingTime - loadingTime);
      } else {
        setIsLoading(false);
      }
    }
  };

  const handlePOIClick = (poi) => {
    setSelectedPOI(poi);
  };

  const handleCloseModal = () => {
    setSelectedPOI(null);
  };

  const handleFavoriteRemoved = async () => {
    if (!selectedPOI) return;
    
    try {
      // Use cache service method which automatically invalidates cache
      await cacheService.removeFavorite(selectedPOI.id);
      
      setFavorites(prevFavorites => 
        prevFavorites.filter(fav => fav.id !== selectedPOI.id)
      );
      setToastMessage(translations[language].poi.removeFromFavorites);
      setShowToast(true);
      setSelectedPOI(null);
    } catch (err) {
      console.error('Error removing favorite:', err);
      setToastMessage(translations[language].common.error);
      setShowToast(true);
    }
  };

  const getPoiImage = (poi) => {
    if (!poi.image_url) return '';
    try {
      const images = JSON.parse(poi.image_url);
      return Array.isArray(images) ? images[0] : poi.image_url;
    } catch {
      return poi.image_url;
    }
  };

  if (isLoading) return (
    <PageWrapper>
      <Navbar />
      <AdPageLayout>
        <PageContainer>
          <PageTitle>{t.title}</PageTitle>
          <SkeletonLoading />
        </PageContainer>
      </AdPageLayout>
    </PageWrapper>
  );

  if (error) return (
    <PageWrapper>
      <Navbar />
      <AdPageLayout>
        <ErrorMessage>{error}</ErrorMessage>
      </AdPageLayout>
    </PageWrapper>
  );

  return (
    <PageWrapper>
      <Navbar />
      <AdPageLayout>
        <PageContainer>
          <PageHeader>
            <PageTitle>{t.title}</PageTitle>
          </PageHeader>
          {favorites.length === 0 ? (
            <EmptyStateContainer>
              <EmptyStateIcon>❤️</EmptyStateIcon>
              <EmptyStateTitle>{t.noFavorites}</EmptyStateTitle>
              <EmptyStateText>
                {t.noFavorites}
              </EmptyStateText>
              <ExploreButton onClick={() => window.location.href = '/'}>
                {translations[language].poi.showMore}
              </ExploreButton>
            </EmptyStateContainer>
          ) : (
            <FavoritesGrid>
              {favorites.map(poi => (
                <FavoriteCard key={poi.id} onClick={() => setSelectedPOI(poi)}>
                  <FavoriteImageContainer>
                    <FavoriteImage src={getPoiImage(poi)} alt={getLocalizedField(poi, 'name')} />
                  </FavoriteImageContainer>
                  <CardBody>
                    <FavoriteName>{getLocalizedField(poi, 'name')}</FavoriteName>
                    <FavoriteDescription>{getLocalizedField(poi, 'description')}</FavoriteDescription>
                  </CardBody>
                </FavoriteCard>
              ))}
            </FavoritesGrid>
          )}
          {selectedPOI && (
            <POIDetailsSidePanel
              poi={selectedPOI}
              onClose={() => setSelectedPOI(null)}
              onFavoriteRemoved={handleFavoriteRemoved}
              onShowToast={(message, type) => {
                setToastMessage(message);
                setShowToast(true);
              }}
            />
          )}
          {showToast && (
            <Toast
              message={toastMessage}
              onClose={() => setShowToast(false)}
              duration={3000}
            />
          )}
        </PageContainer>
      </AdPageLayout>
      <MobileAppBanner />
    </PageWrapper>
  );
};

export default FavoritesPage;
